import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { subscribeNewsletter, klaviyoIdentify, postKlaviyo } from '@/services/forms.service';
import { getCountry } from '@/utils/i18n';
import Button from '@/components/homepage/Button';
import Input, { InputType } from '@/components/Form/Input';
import Title from '@/components/Title/Title';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId } from '@/utils/builderUtils';
import useStore from '@/store/useStore';
import RichText from '@/components/RichText/RichText';
import { FadeIn } from '@/components/Footer/components/FadeIn';
import { sha256 } from '@/utils/utils';
import useMD5HashEmail from '@/hooks/useMD5HashEmail';

interface IEmailSignUpForm {
  submitLabel?: string;
  title: string;
  description: string;
  successText: string;
  cta: {
    variant: 'primary' | 'secondary';
    color: 'light' | 'dark';
    label: string;
  };
  anchorLinkID?: string;
}

export default function EmailSignUpForm({ title, description, cta, successText, anchorLinkID }: IEmailSignUpForm) {
  const [submitLabel, setSubmitLabel] = useState('');
  const user = useStore(state => state.user);
  const isEurope = getCountry().code === 'GB' || user?.isEuropeanUnionMember;
  const [formSettled, setFormSettled] = useState(false);
  const { hashEmail } = useMD5HashEmail();

  function centerSuccessText() {
    setTimeout(() => {
      const element = document.getElementById('success-text')!;
      element.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
    }, 100);
  }

  const inputs: InputType[] = [
    {
      name: 'email',
      id: 'email',
      label: 'Enter your email address',
      fullWidth: true,
      type: 'email',
      registerObject: {
        required: true,
        noRequiredSymbol: true,
        validate: {
          isValid: v => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) || 'Invalid Email Address.',
        },
      },
    },
  ];

  const {
    register,
    getValues,
    trigger,
    setFocus,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { subscription: false } });

  if (isEurope) {
    inputs.push({
      name: 'privacyPolicyTermsConditions',
      id: 'privacyPolicyTermsConditions',
      fullWidth: true,
      type: 'checkbox',
      className: 'tw-mt-[-12px] [&>p]:tw-pt-0',
      label:
        "By checking this box and submitting my email address, I agree to Brilliant Earth's <a href='https://www.brilliantearth.com/en-gb/security-and-privacy-policies/#privacy' target='_blank'>Privacy Policy</a> and <a href='https://www.brilliantearth.com/en-gb/terms-and-conditions/' target='_blank'>Terms and Conditions</a>.",
      registerObject: {
        required: 'Agreement to our Privacy Policy and Terms and Conditions is required.',
      },
    });
  }
  const [isSubmitting, setIsSubmitting] = useState(false);
  async function submitForm() {
    const isFormValid = await trigger();
    const { email } = getValues() as any;
    if (isFormValid) {
      setIsSubmitting(true);
      setSubmitLabel('Sending');

      klaviyoIdentify({ email });
      postKlaviyo('Blog Keep in Touch', email);
      subscribeNewsletter(email)
        .then(async () => {
          const hashedEmail = await hashEmail(email);
          (window as any).dataLayer.push({
            userEmail: email,
            userHashedEmail: await sha256(email),
            userPhoneNumber: '',
            userFirstName: '',
            userLastName: '',
            hashed_user_email_md5: hashedEmail,
          });
          (window as any).dataLayer.push({ event: 'Subscribe via Footer' });
          setFormSettled(true);
          reset();
          setSubmitLabel('Sent Successfully');
          centerSuccessText();
        })
        .catch(() => {
          // eslint-disable-next-line no-alert
          alert('Sent failed. Please try again.');
        })
        .finally(() => {
          setSubmitLabel(cta?.label);
          setIsSubmitting(false);
        });
    }
  }
  useEffect(() => {
    const firstError = Object.keys(errors).reduce<any>((field, a) => (errors[field] ? field : a), null);
    if (firstError) {
      setFocus<any>(firstError);
    }
  }, [errors, setFocus]);

  return (
    <section
      className={beTwMerge('sm:tw-w-[602px] tw-mx-4 sm:tw-mx-auto tw-mb-14 tw-mt-6 sm:tw-mb-20 sm:tw-mt-10')}
      {...getAnchorId(anchorLinkID)}
    >
      <FadeIn props={{ opacity: formSettled ? 1 : 0 }} visible={formSettled}>
        <p id="success-text" className="tw-text-center">
          {successText}
        </p>
      </FadeIn>
      {!formSettled && (
        <div className="tw-w-full">
          <Title component="h2" weight="normal" size="large" alignment="center" className="!tw-mb-2">
            {title}
          </Title>
          <RichText className="tw-font-normal tw-text-center tw-mb-[3.25rem]" content={description} />
          <form
            onSubmit={e => {
              e.preventDefault();
              submitForm();
            }}
          >
            <div className="tw-grid md:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-9 tw-mb-6 sm:tw-mb-8">
              {inputs.map(input => (
                <>
                  {/* @ts-ignore */}
                  <Input
                    {...input}
                    key={input.name}
                    register={register as any}
                    name={input.name}
                    id={input.id}
                    className={input.className}
                    error={errors[input.name] as any}
                  />
                </>
              ))}
            </div>

            <div className="tw-col-span-full tw-flex tw-justify-center">
              <Button
                className="tw-min-w-[200px]"
                label={submitLabel || cta?.label}
                color={cta?.color === 'light' ? 'white' : 'dark-green'}
                type={cta?.variant}
                onClick={submitForm}
                disabled={isSubmitting}
              />
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
